/**
 * CoreREST API Client
 *
 * Handles all HTTP communication with the StreamSyncEngage backend API.
 *
 * NOTE: JWT token injection is handled automatically by Neodigm 55's mvvLegit
 * mvvLegit overloads the global fetch() function to automatically inject Bearer tokens
 * for all requests to the configured BASE URL.
 *
 * Features:
 * - Error handling and token expiration detection
 * - JSON request/response handling
 * - Integration with pub/sub event system
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';

// API Base URL - client ALWAYS uses production API
// RULE: All client apps point to hosted API (no localhost fallback)
const API_BASE_URL = 'https://streamsyncengage-saas.onrender.com';

/**
 * CoreREST API Client Class
 */
class CoreRestAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Generic fetch wrapper with error handling
   *
   * NOTE: Bearer token is automatically injected by mvvLegit's fetch overload
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Default headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      // Handle 401/403 - token expired or invalid
      if (response.status === 401 || response.status === 403) {
        PubSub.publish(AppBroadcast.AUTH_TOKEN_EXPIRED, {
          status: response.status,
          endpoint
        });

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Authentication failed');
      }

      // Parse JSON response
      const data = await response.json();

      // Return response with status info
      return {
        ok: response.ok,
        status: response.status,
        data
      };

    } catch (error) {
      console.error('API Request Error:', error);

      // Publish error event
      PubSub.publish(AppBroadcast.APP_ERROR, {
        message: error.message,
        endpoint
      });

      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  // ============================================
  // Authentication API Methods
  // ============================================

  /**
   * Sign in
   */
  async signin(email, passwordHash) {
    return this.post('/api/acctEntity/signin', {
      email,
      hash: passwordHash
    });
  }

  /**
   * Sign up (create new account)
   */
  async signup(accountData) {
    return this.post('/api/acctEntity/signup', accountData);
  }

  /**
   * Sign out
   */
  async signout() {
    return this.post('/api/acctEntity/signout', {});
  }

  /**
   * Request password reset (forgot password)
   */
  async forgotPassword(email) {
    return this.post('/api/acctEntity/forgot', {
      email
    });
  }

  /**
   * Reset password hash
   */
  async resetHash(email, newPasswordHash) {
    return this.post('/api/acctEntity/resethash', {
      email,
      hash: newPasswordHash
    });
  }

  /**
   * Verify account email
   */
  async verifyAccount(code) {
    return this.get(`/api/acctEntity/verify?CODE=${code}`);
  }

  // ============================================
  // Account Management API Methods
  // ============================================

  /**
   * Get all accounts (requires auth)
   */
  async getAccounts() {
    return this.get('/api/acctEntity');
  }

  /**
   * Get account by ID (requires auth)
   */
  async getAccount(id) {
    return this.get(`/api/acctEntity/${id}`);
  }

  /**
   * Update account (requires auth)
   */
  async updateAccount(id, accountData) {
    return this.put(`/api/acctEntity/${id}`, accountData);
  }

  /**
   * Delete account (requires auth)
   */
  async deleteAccount(id) {
    return this.delete(`/api/acctEntity/${id}`);
  }

  // ============================================
  // Health Check
  // ============================================

  /**
   * Ping server (health check)
   */
  async ping() {
    return this.get('/ping');
  }
}

// Export singleton instance
export default new CoreRestAPI();
